import type { Barber, SalonSlug } from "./types";
import vahidPhoto from "@/assets/opt/vahid-light.webp";
import tonyPhoto from "@/assets/opt/tony-flipped-light.webp";
import aliPhoto from "@/assets/opt/ali-bokadirekt.webp";
import sammyPhoto from "@/assets/opt/sammy-taby.webp";
import erfisPhoto from "@/assets/opt/erfis-taby.webp";
import timPhoto from "@/assets/opt/tim-tailors-barber.webp.asset.json";
import hussePhoto from "@/assets/opt/husse-tailors-barber.webp.asset.json";

/**
 * ENDA KÄLLAN för barberare.
 *
 * Källa: BokaDirekts personalvyer, avläst 2026-08-28.
 * Betyg och antal omdömen är PER BARBERARE och följer personen, inte salongen —
 * därför förekommer samma barberare på flera salongssidor med samma siffror.
 *
 * Regler (får inte brytas):
 *  - Endast barberare med uppladdat foto renderas. Saknas `photo` utelämnas
 *    personen helt — aldrig platshållare.
 *  - Sortering: betyg fallande, vid lika betyg flest omdömen först.
 *    Barberare utan betyg (nya) hamnar sist och visas utan betygssiffra.
 *  - Undantag från betygssorteringen: `pinnedPosition` placerar en barberare
 *    på en bestämd plats i rutnätet (räknat från 1), per salong via en karta
 *    med salongsnyckel (t.ex. `{ "ahlens-city": 3 }`). Det är ett medvetet
 *    val, inte en bugg: pinnade hamnar på sin plats efter att övriga
 *    sorterats på betyg, och övriga knuffas ned i samma inbördes ordning.
 *    Pinnen gäller bara den salong som renderas; på övriga salonger ligger
 *    personen i vanlig betygsordning. Saknas fältet eller salongsnyckeln
 *    gäller betygssorteringen som vanligt.
 *  - Salongens omdömestal är summan av barberarnas omdömen och får ALDRIG
 *    beskrivas som salongens egna. Använd `salonBarberLine()`.
 *  - Summera aldrig salongernas tal till en kedjesiffra — samma barberare
 *    räknas på flera salonger (dubbelräkning).
 */

const PLACE: Record<SalonSlug, string> = {
  "ahlens-city": "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678",
  taby: "https://www.bokadirekt.se/places/tailors-barber-taby-centrum-37677",
  akersberga: "https://www.bokadirekt.se/places/tailors-barber-akersberga-ica-maxi-huset--37682",
  arninge: "https://www.bokadirekt.se/places/tailors-barber-arninge-ica-maxi-huset--61598",
};

const SALON_NAME: Record<SalonSlug, string> = {
  "ahlens-city": "Åhléns City",
  taby: "Täby Centrum",
  akersberga: "Åkersberga",
  arninge: "Arninge",
};

/** BokaDirekt-länk till personens sida på en viss salong (staff-id om känt). */
function staffUrl(slug: SalonSlug, staffId?: string): string {
  return staffId ? `${PLACE[slug]}?staff=${staffId}` : PLACE[slug];
}

interface StaffMember {
  id: string;
  name: string;
  /** Utelämna helt tills porträtt finns — personen renderas då inte. */
  photo?: string;
  bio: string;
  /** Saknas för nya barberare utan omdömen än. */
  rating?: number;
  reviewCount?: number;
  specialties: string[];
  /** Språk personen talar med kunderna. Lämnas bort tills uppgifter finns. */
  languages?: string[];
  /** Medveten undantagsplats i rutnätet (1-baserad), per salong via salongsnyckel.
   *  Åsidosätter betygssorteringen för personen på den salongen; övriga knuffas ned
   *  i samma ordning. På salonger utan nyckel ligger personen i vanlig betygsordning. */
  pinnedPosition?: Partial<Record<SalonSlug, number>>;
  /** Salonger personen jobbar på + ev. BokaDirekt staff-id per salong. */
  at: Partial<Record<SalonSlug, { staffId?: string }>>;
  photoZoom?: number;
  photoYOffset?: string;
}

const CDN = "https://d8j0ntlcm91z4.cloudfront.net/user_38sgKIGFipLVVm4ZhDh7ZaR3pqy";

export const staff: StaffMember[] = [
  {
    id: "ali",
    name: "Ali",
    photo: aliPhoto,
    bio: "Ali är en uppskattad barberare i Täby Centrum med hundratals nöjda kunder – känd för noggranna klippningar och ett välkomnande bemötande.",
    rating: 4.9,
    reviewCount: 933,
    specialties: ["Herrklippning", "Fade", "Skägg"],
    at: { taby: { staffId: "61106" } },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "william",
    name: "William",
    photo: `${CDN}/hf_20260704_173055_4ce4b793-b332-452d-a8c3-e9b092ed09fe.png`,
    bio: "William är en riktig all around barber som behärskar allt från klassiska klippningar till moderna fades – pratglad och lätt att trivas med i stolen.",
    rating: 4.9,
    reviewCount: 539,
    specialties: ["Herrklippning", "Fade", "Taper Fade"],
    at: { taby: { staffId: "247138" }, arninge: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "neo",
    name: "Neo",
    photo: `${CDN}/hf_20260704_173052_d2cb5e1e-79c1-4953-960d-1656259d1373.png`,
    bio: "Neo är en barberare som klipper moderna frisyrer med skarp känsla för form och detalj – ett självklart val för dig som vill ha ett samtida och välformat resultat.",
    rating: 4.9,
    reviewCount: 389,
    pinnedPosition: { "ahlens-city": 5 },
    specialties: ["Taper Fade", "Skin Fade", "Herrklippning"],
    languages: ["Svenska", "Engelska", "Spanska"],
    at: { "ahlens-city": { staffId: "194561" }, taby: { staffId: "194560" } },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "tony",
    name: "Tony",
    photo: tonyPhoto,
    bio: "Tony är en barberare som är väldigt bra med barn – pratglad, trevlig och hjälpsam, och skapar en trygg och avslappnad stämning för både yngre och äldre kunder.",
    rating: 4.9,
    reviewCount: 200,
    specialties: ["Herrklippning", "Barnklippning", "Fade"],
    at: { taby: { staffId: "307049" }, akersberga: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "regla",
    name: "Regla",
    photo: `${CDN}/hf_20260704_173045_49d85e3f-4a04-4bf5-82d6-d2a4471eb8a0.png`,
    bio: "Regla är en barberare känd för precision och ett varmt, omsorgsfullt bemötande – alltid med fokus på att leverera ett resultat kunden är stolt över.",
    rating: 4.9,
    reviewCount: 137,
    specialties: ["Herrklippning", "Taper Fade", "Signature Cut"],
    languages: ["Svenska", "Engelska", "Spanska"],
    at: { "ahlens-city": { staffId: "61129" } },
    photoZoom: 1.2,
    photoYOffset: "8%",
  },
  {
    id: "erfis",
    name: "Erfis",
    photo: erfisPhoto,
    bio: "Erfis är en ny barberare i Täby Centrum – välkomnande och skicklig, med fokus på att leverera ett klipp du blir nöjd med varje gång.",
    rating: 5.0,
    reviewCount: 9,
    specialties: ["Herrklippning", "Fade", "Skägg"],
    at: { taby: { staffId: "334055" } },
    photoZoom: 1.0,
    photoYOffset: "8%",
  },
  {
    id: "jonathan",
    name: "Jonathan",
    // Foto saknas hos BokaDirekt – renderas inte förrän porträtt laddats upp.
    bio: "Jonathan är ett av Tailor's Barbers nyaste tillskott – noggrann och engagerad i varje klippning.",
    rating: 5.0,
    reviewCount: 5,
    specialties: ["Herrklippning", "Fade"],
    at: { taby: {}, akersberga: {} },
  },
  {
    id: "yannis",
    name: "Yannis",
    photo: `${CDN}/hf_20260704_173042_653ed596-a814-47a1-9f3f-551d34781d45.png`,
    bio: "Yannis är en lyhörd och erfaren barberare, duktig på styling och produkter – ger dig en bekväm upplevelse från stol till spegel.",
    rating: 4.8,
    reviewCount: 344,
    specialties: ["Herrklippning", "Hår & Skägg", "Skägg"],
    languages: ["Engelska", "Grekiska", "Ryska"],
    at: { "ahlens-city": { staffId: "270796" } },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "tara",
    name: "Tara",
    photo: `${CDN}/hf_20260704_175314_9b03e22f-92f9-4f0b-8695-c0f0f7bb333d.png`,
    bio: "Tara är en barberare som snabbt byggt upp en lojal kundkrets – känd för att alltid leverera ett resultat kunden är stolt över.",
    rating: 4.8,
    reviewCount: 284,
    specialties: ["Herrklippning", "Fade", "Barnklippning"],
    languages: ["Svenska", "Engelska", "Farsi"],
    pinnedPosition: { "ahlens-city": 6 },
    at: { "ahlens-city": { staffId: "79622" } },
    photoZoom: 3.2,
    photoYOffset: "40%",
  },
  {
    id: "sam",
    name: "Sam",
    photo: `${CDN}/hf_20260704_173044_9a1204b1-618e-452a-84b9-ca52554eeac7.png`,
    bio: "Sam är en pratglad och lyhörd barberare med öga för detaljer – behärskar både klassiska och moderna klippningar.",
    rating: 4.8,
    reviewCount: 278,
    specialties: ["Herrklippning", "Fade", "Skägg"],
    languages: ["Svenska", "Engelska"],
    pinnedPosition: { "ahlens-city": 4 },
    at: { "ahlens-city": { staffId: "253377" } },
    photoZoom: 1.05,
    photoYOffset: "32%",
  },
  {
    id: "nemat",
    name: "Nemat",
    photo: `${CDN}/hf_20260704_173102_f4758749-b0da-4234-b042-100e44407cf0.png`,
    bio: "Nemat är en erfaren och uppskattad barberare med lång rutin i stolen – lugn, noggrann och pålitlig i varje klipp.",
    rating: 4.8,
    reviewCount: 179,
    specialties: ["Herrklippning", "Skägg", "Slätrakning"],
    at: { taby: { staffId: "183662" } },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "vahid",
    name: "Vahid",
    photo: vahidPhoto,
    bio: "Vahid är en skicklig och engagerad barberare med känsla för detaljer – alltid med fokus på att du ska lämna salongen nöjd.",
    rating: 4.8,
    reviewCount: 594,
    specialties: ["Herrklippning", "Skin Fade", "Skägg"],
    at: { arninge: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "pringles",
    name: "Pringles",
    photo: `${CDN}/hf_20260704_173100_f83dbff0-77bc-462e-a0c2-5359eca007d5.png`,
    bio: "Pringles är en expert på produkter och styling – välkomnande och varm i bemötandet, och hjälper dig gärna att hitta rätt rutin för både hår och skägg.",
    rating: 4.8,
    reviewCount: 140,
    specialties: ["Herrklippning", "Styling", "Skägg"],
    at: { arninge: {}, taby: { staffId: "305867" } },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "nick",
    name: "Nick",
    photo: `${CDN}/hf_20260704_173050_539fda69-61d9-4801-90c4-d036274b31ee.png`,
    bio: "Nick är en skicklig, pratglad och välkomnande barberare som får dig att känna dig avslappnad direkt och samtidigt levererar ett klipp du blir nöjd med.",
    rating: 4.8,
    reviewCount: 64,
    specialties: ["Herrklippning", "Fade", "Taper Fade"],
    languages: ["Engelska", "Grekiska", "Albanska"],
    at: { "ahlens-city": { staffId: "325043" }, taby: { staffId: "322783" } },
    photoZoom: 1.0,
    photoYOffset: "10%",
  },
  {
    id: "husse",
    name: "Husse",
    photo: hussePhoto.url,
    bio: "Husse är en erfaren barberare med trogna kunder på både Åhléns City och Täby Centrum.",
    rating: 4.7,
    reviewCount: 312,
    specialties: ["Herrklippning", "Skägg", "Fade"],
    languages: ["Svenska", "Engelska", "Farsi"],
    at: { "ahlens-city": {}, taby: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "ahmed",
    name: "Ahmed",
    // Foto saknas hos BokaDirekt – renderas inte förrän porträtt laddats upp.
    bio: "Ahmed är en erfaren barberare i Arninge med säker hand och personligt bemötande.",
    rating: 4.7,
    reviewCount: 276,
    specialties: ["Herrklippning", "Fade", "Skägg"],
    at: { arninge: {} },
  },
  {
    id: "adam",
    name: "Adam",
    photo: `${CDN}/hf_20260704_173106_cf997107-9a07-4376-a4ef-fe11a1af8903.png`,
    bio: "Adam är en erfaren och uppskattad barberare med säker hand och personligt bemötande – tar sig tid att göra jobbet rätt från start till slut.",
    rating: 4.7,
    reviewCount: 53,
    specialties: ["Herrklippning", "Fade", "Skägg"],
    at: { taby: { staffId: "319760" }, akersberga: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "sammy",
    name: "Sammy",
    photo: sammyPhoto,
    bio: "Sammy är ett av Tailor's Barbers nyaste tillskott – engagerad, noggrann och med ett öga för moderna klippningar och fades.",
    rating: 4.7,
    reviewCount: 9,
    specialties: ["Herrklippning", "Fade", "Skin Fade"],
    at: { taby: { staffId: "332864" }, akersberga: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "diana",
    name: "Diana",
    photo: `${CDN}/hf_20260704_173059_5aa5453b-9682-49ba-8b74-d34e5acad7ce.png`,
    bio: "Diana är en erfaren och mycket uppskattad barberare med en trogen kundkrets – känd för att leverera ett stabilt och snyggt resultat gång på gång.",
    rating: 4.6,
    reviewCount: 901,
    specialties: ["Herrklippning", "Fade", "Barnklippning"],
    at: { taby: { staffId: "61109" }, akersberga: {} },
    photoZoom: 1.3,
    photoYOffset: "5%",
  },
  {
    id: "dia",
    name: "Dia",
    photo: `${CDN}/hf_20260704_173111_fc83467f-efd7-41f7-a7e1-d43bd15e04d1.png`,
    bio: "Dia är en erfaren och uppskattad barberare med säker teknik och personlig service – levererar konsekvent bra resultat gång på gång.",
    rating: 4.6,
    reviewCount: 329,
    specialties: ["Herrklippning", "Fade", "Skägg"],
    at: { akersberga: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "sami",
    name: "Sami",
    photo: `${CDN}/hf_20260704_173109_3fc489d8-7070-43b7-b63a-c4fd152aa338.png`,
    bio: "Sami är en erfaren och uppskattad barberare med lång rutin och trogna kunder – noggrann, pålitlig och lätt att prata med.",
    rating: 4.6,
    reviewCount: 301,
    specialties: ["Herrklippning", "Skägg", "Fade"],
    at: { akersberga: {}, arninge: {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "adonis",
    name: "Adonis",
    photo: `${CDN}/hf_20260704_173054_b5cda256-e75f-4372-b1a5-2b53c09a9fa1.png`,
    bio: "Adonis är en erfaren och uppskattad barberare med säker hand och gott öga för detaljer – ett tryggt val oavsett om det gäller klippning eller skägg.",
    rating: 4.6,
    reviewCount: 286,
    specialties: ["Herrklippning", "Taper Fade", "Fade"],
    languages: ["Svenska", "Engelska", "Farsi"],
    at: { "ahlens-city": { staffId: "266029" }, taby: { staffId: "255153" } },
    photoZoom: 1.1,
    photoYOffset: "0%",
  },
  {
    id: "valle",
    name: "Valle",
    photo: `${CDN}/hf_20260704_175643_cf3e0314-e4bf-4fe3-b17a-7f4bf97138e9.png`,
    bio: "Valle är en erfaren och uppskattad barberare som levererar ett prydligt resultat med bra bemötande – ett tryggt val för din nästa klippning.",
    rating: 4.5,
    reviewCount: 43,
    specialties: ["Herrklippning", "Skägg"],
    at: { taby: { staffId: "83829" } },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
  {
    id: "tim",
    name: "Tim",
    photo: timPhoto.url,
    bio: "Tim är ny hos Tailor's Barber på Åhléns City – noggrann, lyhörd och redo att ta hand om ditt nästa klipp.",
    // Inget betyg än – visas utan betygssiffra, aldrig som noll.
    specialties: ["Herrklippning", "Fade"],
    languages: ["Engelska", "Ukrainska"],
    pinnedPosition: { "ahlens-city": 3 },
    at: { "ahlens-city": {} },
    photoZoom: 1.1,
    photoYOffset: "8%",
  },
];

const SALON_ORDER: SalonSlug[] = ["ahlens-city", "taby", "akersberga", "arninge"];

export interface SalonBarber {
  name: string;
  photo?: string;
  rating?: number;
  reviewCount?: number;
  bio?: string;
  languages?: string[];
  photoZoom?: number;
  photoYOffset?: string;
  bookingUrl?: string;
  /** Namn på övriga salonger personen även jobbar på. */
  alsoAt?: string[];
}

/** Betyg fallande, vid lika betyg flest omdömen först, obetygsatta sist. */
function byRating(a: StaffMember, b: StaffMember): number {
  const ra = a.rating ?? -1;
  const rb = b.rating ?? -1;
  if (rb !== ra) return rb - ra;
  return (b.reviewCount ?? 0) - (a.reviewCount ?? 0);
}

function barbersAt(slug: SalonSlug): SalonBarber[] {
  const sorted = staff.filter((s) => s.photo && s.at[slug]).sort(byRating);
  // Pinnade placeras på sin angivna plats (minst pinnad först), och övriga
  // behåller sin inbördes betygsordning och knuffas ned. Pinnen gäller bara
  // den salong som renderas (per salongsnyckel).
  const pinned = sorted
    .filter((s) => s.pinnedPosition?.[slug] != null)
    .sort((a, b) => (a.pinnedPosition?.[slug] ?? 0) - (b.pinnedPosition?.[slug] ?? 0));
  const result = sorted.filter((s) => s.pinnedPosition?.[slug] == null);
  for (const s of pinned) {
    const idx = Math.min(Math.max((s.pinnedPosition?.[slug] ?? 1) - 1, 0), result.length);
    result.splice(idx, 0, s);
  }
  return result.map((s) => {
    const alsoAt = SALON_ORDER.filter((k) => k !== slug && s.at[k]).map((k) => SALON_NAME[k]);
    return {
      name: s.name,
      photo: s.photo,
      rating: s.rating,
      reviewCount: s.reviewCount,
      bio: s.bio,
      languages: s.languages,
      photoZoom: s.photoZoom,
      photoYOffset: s.photoYOffset,
      bookingUrl: staffUrl(slug, s.at[slug]?.staffId),
      alsoAt: alsoAt.length ? alsoAt : undefined,
    };
  });
}

export const SALON_BARBERARE: Record<SalonSlug, { name: string; barbers: SalonBarber[] }> = {
  "ahlens-city": { name: SALON_NAME["ahlens-city"], barbers: barbersAt("ahlens-city") },
  taby: { name: SALON_NAME.taby, barbers: barbersAt("taby") },
  akersberga: { name: SALON_NAME.akersberga, barbers: barbersAt("akersberga") },
  arninge: { name: SALON_NAME.arninge, barbers: barbersAt("arninge") },
};

export type SalonBarberKey = keyof typeof SALON_BARBERARE;

/**
 * Salongens omdömestal enligt BokaDirekts personalvy 2026-08-28 — summan av de
 * betyg barberarna där har samlat, oavsett var. Talet tillhör barberarna, inte
 * salongen: formulera alltid med `salonBarberLine()`.
 * Summera ALDRIG dessa tal till en kedjesiffra — samma barberare räknas på
 * flera salonger (Diana räknas t.ex. både i Täby och Åkersberga).
 */
export const salonBarberStats: Record<SalonSlug, { count: number; rating: number }> = {
  "ahlens-city": { count: 2098, rating: 4.8 },
  taby: { count: 4237, rating: 4.8 },
  akersberga: { count: 1798, rating: 4.6 },
  arninge: { count: 1625, rating: 4.8 },
};

/** "Våra barberare i Arninge har 1 625 omdömen · 4,8 i snitt" */
export function salonBarberLine(slug: SalonSlug): string {
  const { count, rating } = salonBarberStats[slug];
  return `Våra barberare i ${SALON_NAME[slug]} har ${count.toLocaleString("sv-SE")} omdömen · ${rating
    .toFixed(1)
    .replace(".", ",")} i snitt`;
}

/**
 * Formaterar "Talar svenska, engelska och farsi": liten bokstav på språknamnen,
 * komma mellan och "och" före det sista.
 */
export function languagesLine(languages: string[]): string {
  const names = languages.map((l) => l.charAt(0).toLowerCase() + l.slice(1));
  if (names.length === 1) return `Talar ${names[0]}`;
  return `Talar ${names.slice(0, -1).join(", ")} och ${names[names.length - 1]}`;
}

/** Legacy-form som används av SalonPageTemplate. */
export const barbers: Barber[] = staff
  .filter((s) => s.photo)
  .sort(byRating)
  .map((s, i) => ({
    id: s.id,
    name: s.name,
    intro: s.bio,
    specialties: s.specialties,
    salons: SALON_ORDER.filter((k) => s.at[k]),
    bookingUrlBySalon: Object.fromEntries(
      SALON_ORDER.filter((k) => s.at[k]).map((k) => [k, staffUrl(k, s.at[k]?.staffId)]),
    ),
    rating: s.rating,
    reviewCount: s.reviewCount,
    photo: s.photo,
    languages: s.languages,
    active: true,
    displayOrder: i + 1,
  }));

export const barberById = Object.fromEntries(barbers.map((b) => [b.id, b])) as Record<
  string,
  Barber
>;
